import { InstallHint } from "@/ui/InstallHint.tsx";
import { PROVIDERS, type AgentProvider, type PublicUser } from "@codvero/shared";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { FileCode2, Github, MessageSquare, Play, Plus, Settings2, Upload } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ProviderIcon } from "@/ui/ProviderIcon.tsx";
import { accountsApi } from "@/api/accounts.ts";
import { projectsApi } from "@/api/projects.ts";
import { CreateProjectDialog } from "@/features/projects/CreateProjectDialog.tsx";
import { useProjectActions } from "@/features/projects/ProjectPage.tsx";
import { DockerBanner } from "@/features/projects/ProjectsPage.tsx";
import { StateBadge } from "@/features/projects/StateBadge.tsx";
import { MachinesGrid } from "./MachinesGrid.tsx";
import { NewChatComposer } from "@/features/space/SpacePage.tsx";
import { TABLET_QUERY, useMediaQuery } from "@/lib/media.ts";
import { cn } from "@/lib/cn.ts";
import { Button, PageTitle } from "@/ui/primitives.tsx";

/** Home: the original's "Command center" (first run) that becomes the "Machines" grid once projects exist. */
export function CommandCenter({ user }: { user: PublicUser }) {
  const { t } = useTranslation();
  const projects = useQuery({ queryKey: ["projects"], queryFn: projectsApi.list, refetchInterval: 10_000 });
  const accounts = useQuery({ queryKey: ["accounts"], queryFn: accountsApi.list });
  const [creating, setCreating] = useState<null | "blank" | "upload" | "github">(null);
  const list = projects.data ?? [];
  const connectedBy = (p: AgentProvider) => (accounts.data ?? []).some((a) => a.provider === p && a.status === "connected");
  const connected = connectedBy("claude");
  // a new Chat-section conversation from here: above the machines on wider screens, at the thumb on a phone
  const chat = user.features.includes("chat");
  const wide = useMediaQuery(TABLET_QUERY);
  const composer = chat && wide ? <div className="mx-auto w-full max-w-2xl px-2"><NewChatComposer autoFocus={false} lines={2} /></div> : null;

  return (
    <div className="flex h-full flex-col">
    <div className="min-h-0 flex-1 overflow-y-auto">
      <div className="mx-auto max-w-[1600px] space-y-8 px-4 py-6 sm:px-8">
        <InstallHint />
        <DockerBanner />
        {list.length === 0 ? (
          <>
            {composer}
            <PageTitle title={t("home.firstTitle")} subtitle={t("home.firstSubtitle")} />
            <div className="grid gap-3 sm:grid-cols-3">
              <StartCard icon={Plus} title={t("create.blank")} desc={t("create.blankDesc")} onClick={() => setCreating("blank")} />
              <StartCard icon={Upload} title={t("create.upload")} desc={t("create.uploadDesc")} onClick={() => setCreating("upload")} />
              <StartCard icon={Github} title={t("create.github")} desc={t("create.githubDesc")} onClick={() => setCreating("github")} />
            </div>
          </>
        ) : (
          <MachinesGrid onNew={() => setCreating("blank")} above={composer} />
        )}

        <section className="space-y-3">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-fg-muted">{t("home.beforeAgents")}</h2>
          <div className="rounded-lg border border-border bg-surface divide-y divide-border">
            {(Object.keys(PROVIDERS) as AgentProvider[]).map((id) => (
              <ProviderRow key={id} provider={id} name={`${PROVIDERS[id].label}${PROVIDERS[id].beta ? " · beta" : ""}`} status={connectedBy(id) ? t("accounts.status.connected") : t("accounts.status.pending")} ok={connectedBy(id)} muted={!connectedBy(id) && id !== "claude"} />
            ))}
          </div>
        </section>
        <p className="text-xs text-fg-muted">{t("home.welcome", { name: user.name })}</p>
      </div>
    </div>
      {chat && !wide && <div className="shrink-0 border-t border-border bg-bg/95 px-3 pb-2 pt-2 backdrop-blur"><NewChatComposer compact autoFocus={false} /></div>}
      {creating && <CreateProjectDialog initialSource={creating} onClose={() => setCreating(null)} />}
    </div>
  );
}

function StartCard({ icon: Icon, title, desc, onClick, soon }: { icon: typeof Plus; title: string; desc: string; onClick?: () => void; soon?: boolean }) {
  const { t } = useTranslation();
  return (
    <button type="button" disabled={soon} onClick={onClick} className={cn("rounded-lg border border-border bg-surface p-4 text-start hover:border-accent", soon && "cursor-not-allowed opacity-60")}>
      <Icon className="mb-3 size-5 text-accent" />
      <div className="font-medium">{title} {soon && <span className="ms-1 rounded bg-surface-2 px-1 text-[10px] text-fg-muted">{t("common.soon")}</span>}</div>
      <div className="mt-1 text-xs text-fg-muted">{desc}</div>
    </button>
  );
}

function ProviderRow({ provider, name, status, ok, muted }: { provider: string; name: string; status: string; ok: boolean; muted?: boolean }) {
  const { t } = useTranslation();
  return (
    <div className={cn("flex items-center justify-between gap-3 px-4 py-3", muted && "opacity-60")}>
      <div className="flex items-center gap-3">
        <ProviderIcon provider={provider} size="lg" className="size-8" />
        <div>
          <div className="text-sm font-medium">{name}</div>
          <div className="text-xs text-fg-muted">{status}</div>
        </div>
      </div>
      {ok ? <span className="rounded-full bg-success-soft px-2 py-0.5 text-xs text-success">{t("accounts.status.connected")}</span> : !muted && <Link to="/settings" hash="settings-agents" className="rounded-md border border-border px-2 py-1 text-xs hover:bg-surface-2">{t("home.connect")}</Link>}
    </div>
  );
}

function MachineCard({ id, name, slug, state, message, canEdit }: { id: string; name: string; slug: string; state: "none" | "creating" | "running" | "stopped" | "error"; message: string | null; canEdit: boolean }) {
  const { t } = useTranslation();
  const nav = useNavigate();
  const actions = useProjectActions(id);
  return (
    <div className="flex flex-col gap-3 rounded-lg border border-border bg-surface p-4">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <Link to="/projects/$projectId" params={{ projectId: id }} className="block truncate font-medium hover:underline">{name}</Link>
          <div dir="ltr" className="truncate text-start font-mono text-[11px] text-fg-muted">{slug}</div>
        </div>
        <div className="flex items-center gap-1">
          <StateBadge state={state} />
          <Link to="/projects/$projectId/settings" params={{ projectId: id }} aria-label={t("projects.tabs.settings")} className="rounded p-1 text-fg-muted hover:bg-surface-2 hover:text-fg"><Settings2 className="size-4" /></Link>
        </div>
      </div>
      {state === "error" && message && <div dir="ltr" className="truncate rounded bg-danger-soft px-2 py-1 text-start font-mono text-[11px] text-danger">{message}</div>}
      <div className="mt-auto grid grid-cols-2 gap-2">
        <Button size="sm" variant="secondary" onClick={() => void nav({ to: "/projects/$projectId/chat", params: { projectId: id } })}><MessageSquare className="size-3.5" /> {t("projects.tabs.chat")}</Button>
        {canEdit && (state === "running" ? (
          <Button size="sm" variant="ghost" loading={actions.stop.isPending} onClick={() => actions.stop.mutate()}>{t("projects.stop")}</Button>
        ) : (
          <Button size="sm" loading={actions.start.isPending || state === "creating"} onClick={() => actions.start.mutate()}><Play className="size-3.5" /> {t("projects.start")}</Button>
        ))}
      </div>
    </div>
  );
}
